import { useSelector } from 'react-redux';

import { IState } from 'models/reducers/i-state.interface';
import { ITopOffersTradePreviewDetails } from 'models/top-offers/i-top-offers';
import { ITradePreviewDetails } from 'models/trade/i-trade-preview-details.interface';
import { IWalletCurrency } from 'models/wallet-currency-response.interface';

import { IEvents } from 'hooks/events/useEventsParams';
import { SourceScreenName } from 'hooks/events/useFundsEvents';
import { usePurchase } from 'hooks/usePurchase';
import { useTraits } from 'hooks/useTraits';

import ModalPortal from '../ModalPortal';
import CheckoutFundsModal from './checkoutFundsModal/CheckoutFundsModal';
import CheckoutModalSuccess, { ICheckoutModalSuccessData } from './checkoutModalSuccess/CheckoutModalSuccess';
import CheckoutProgressModal from './checkoutProgressModal/CheckoutProgressModal';
import './CompleteCheckoutOfferModal.scss';
import PurchaseCheckout from './purchaseCheckout/PurchaseCheckout';

interface ICompleteCheckoutOfferModal {
  onClose: () => void;
  onFinished: () => void;
  tradePreviewDetails: ITradePreviewDetails | ITopOffersTradePreviewDetails;
  events?: IEvents;
}

const CompleteCheckoutOfferModal = ({
  tradePreviewDetails,
  onClose,
  onFinished,
  events,
}: ICompleteCheckoutOfferModal) => {
  const currencies = useSelector((state: IState): IWalletCurrency[] | null => state.wallet.currencies);
  const { category, id } = useTraits(tradePreviewDetails.assetDetails.traits);

  const {
    confirmationPending,
    fundsRemaining,
    fundsRemainingCurrency,
    isAddFundsOpen,
    isPurchaseFinished,
    isCheckoutInProgress,
    isPurchaseInProgress,
    progressStep,
    purchase,
    setIsAddFundsOpen,
    transactionHash,
  } = usePurchase(tradePreviewDetails, events);

  const successModalDataMapper = (): ICheckoutModalSuccessData => {
    return {
      totalShares: +tradePreviewDetails.assetDetails.total_shares,
      id: +id,
      address: tradePreviewDetails.assetDetails.address,
      cardUrl: tradePreviewDetails.assetDetails.image_original_url,
      category: category,
      name: tradePreviewDetails.assetDetails.name,
      iconUrl: tradePreviewDetails.assetDetails.marker_url,
      token_id: tradePreviewDetails.assetDetails.token_id!,
      amount: +tradePreviewDetails.tradeDetails.amount!,
    };
  };

  const handleClose = () => {
    onFinished();
    onClose();
  };

  return (
    <>
      {isAddFundsOpen && (
        <CheckoutFundsModal
          fundsRemaining={fundsRemaining}
          currency={fundsRemainingCurrency}
          onContinue={() => setIsAddFundsOpen(false)}
          onClose={onClose}
          sourceScreen={SourceScreenName.CompleteCheckoutModal}
        />
      )}
      {isPurchaseInProgress() && (
        <ModalPortal
          title="your purchase is processing..."
          className="checkout-modal checkout-modal--pending"
          onClose={onClose}
        >
          <CheckoutProgressModal
            checkoutProgressStep={progressStep!}
            transactionHash={transactionHash}
            venueAddress={tradePreviewDetails.assetDetails.address}
            venueName={tradePreviewDetails.assetDetails.name}
            venueShares={+(tradePreviewDetails.tradeDetails?.amount ?? 0)}
            action="purchase"
          />
        </ModalPortal>
      )}
      {isCheckoutInProgress() && (
        <ModalPortal title="Complete checkout" className="checkout-modal" onClose={onClose}>
          {currencies ? (
            <PurchaseCheckout
              confirmationPending={confirmationPending}
              buyOffer={purchase}
              tradePreviewDetails={tradePreviewDetails}
            />
          ) : (
            <></>
          )}
        </ModalPortal>
      )}
      {isPurchaseFinished() && (
        <ModalPortal
          title="your purchase is complete!"
          className="checkout-modal checkout-modal--success"
          onClose={handleClose}
        >
          <CheckoutModalSuccess
            transactionHash={transactionHash}
            checkoutSuccessData={successModalDataMapper()}
            action="purchase"
          />
        </ModalPortal>
      )}
    </>
  );
};
export default CompleteCheckoutOfferModal;
